import styled from 'styled-components';
import noop from 'lodash/noop';
import get from 'lodash/get';
import * as TrackingService from 'common/services/tracking.service';
import { getColor } from 'common/constants/_colors';
import {
  TRACKING,
  PM_METRICS,
  DASHBOARD_INTERNAL_TYPES
} from 'common/constants/constants';
import CustomDashboardSideBar from './CustomDashboardsSideBar';
import { DashboardTab } from './DashboardTab';
import { Tooltip } from '@material-ui/core';
import DashboardSummaryTooltip from '../performance/views/CustomReport/DashboardSummaryTooltip';
import { checkIfIsPmCustomDashboard } from './views/CustomReport/custom-report.serivce';
import { useDispatch, useSelector } from 'react-redux';
import {
  isPmToolJiraSelector,
  pmIntegrationSelector
} from 'state/selectors/sensors-authorizations';
import {
  CUSTOM_DASHBOARD_INTERNAL_TYPES,
  FEATURE_FLAGS_NAMES
} from '@linearb/linjs-common';
import ProjectManagementRetirementTooltip from './ProjectManagementRetirementTooltip';
import DisabledDashboardTab from './DisabledDashboardTab';
import { updatePinnedBoards } from 'state/callers';
import {
  StyledPerformanceSidebar,
  StyledScrollableSection
} from './PerformanceSideBar.style';
import { useEffect, useRef, useState } from 'react';
import useSettingsService from 'components/hooks/useSettingsService';

const LightBorderBottom = styled.div`
  border-bottom: 1px solid ${getColor('gray', 0.2)};
`;

const PerformanceSideBar = ({
  reports = [],
  customDashboards = [],
  selectedReport = null,
  onSelectReport = r => noop,
  onAddReport,
  newCustomDashBoard,
  loaded,
  maxDashboards,
  pinnedDashboards,
  canCreateMoreCustomDashboards,
  shouldHideProjectManagement
}) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const dispatch = useDispatch();
  const pmSensor = useSelector(pmIntegrationSelector);
  const isJira = useSelector(isPmToolJiraSelector);
  const { getSettingOrDefinitionValue } = useSettingsService();

  const showPMReports = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.showPMReports
  });

  useEffect(() => {
    // Set the side bar width whenever window size changes
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef]);

  // if showPMReports - return all reports. if !showPMReports - return all except type = 'pm'
  const filteredPMReports = reports.filter(
    ({ type }) => !(type === 'pm' && !showPMReports)
  );

  const allReportsAndCustomDashboards = [
    ...filteredPMReports,
    ...customDashboards
  ].filter(
    ({ internal_type: internalType }) =>
      internalType !== CUSTOM_DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS ||
      pmSensor
  );

  const allReports = allReportsAndCustomDashboards.filter(
    dashboard => !pinnedDashboards.includes(dashboard.id)
  );

  const defaultFavoritesReports = allReports.filter(
    ({ internal_type, auto_created }) =>
      internal_type === DASHBOARD_INTERNAL_TYPES.TEAM_GOALS ||
      (internal_type === DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS &&
        auto_created)
  );

  const allReportsWithoutTeamGoals = allReports.filter(
    ({ internal_type, auto_created }) =>
      (internal_type !== DASHBOARD_INTERNAL_TYPES.TEAM_GOALS &&
        internal_type === DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS &&
        !auto_created) ||
      !internal_type
  );

  const allFavoritesDashboards = allReportsAndCustomDashboards.filter(
    dashboard => pinnedDashboards.includes(dashboard.id)
  );

  const allFavoritesWithTeamGoals = [
    ...defaultFavoritesReports,
    ...allFavoritesDashboards
  ];

  const routeToCustomPmMetrics = () => {
    const defaultCustomPmReport = customDashboards.find(
      ({ internal_type: internalType, extended_data: extendedData }) =>
        internalType === CUSTOM_DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS &&
        get(extendedData, 'auto_created', true)
    );
    onSelectReport(defaultCustomPmReport);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  return (
    <StyledPerformanceSidebar>
      <CustomDashboardSideBar
        loaded={loaded}
        maxDashboards={maxDashboards}
        favoriteDashboards={allFavoritesWithTeamGoals}
        showEmptyState={customDashboards.length === 0}
        newCustomDashBoard={newCustomDashBoard}
        selectedDashboard={selectedReport}
        onSelectDashboard={onSelectReport}
        onAddDashboard={onAddReport}
        pinnedDashboards={pinnedDashboards}
        canCreateDashboards={canCreateMoreCustomDashboards}
        containerWidth={containerWidth}
      />

      <LightBorderBottom />

      <StyledScrollableSection>
        <div
          ref={containerRef}
          favoritesDashboardsLengh={allFavoritesWithTeamGoals?.length ?? 2}
        >
          {allReportsWithoutTeamGoals.map((report, idx) => {
            const projectManagementDisabled =
              shouldHideProjectManagement && report.id === 'pm';

            const isCustomPmReport = checkIfIsPmCustomDashboard({ report });
            const isMetricReport =
              report.type === 'metrics' || report.type === PM_METRICS;

            if (projectManagementDisabled) {
              return (
                isJira && (
                  <Tooltip
                    title={
                      <ProjectManagementRetirementTooltip
                        routeToCustomPmMetrics={routeToCustomPmMetrics}
                      />
                    }
                    placement='right'
                    interactive
                    key={`${idx}-allReports-${report.account_id || 'linearB'}`}
                  >
                    <div>
                      <DisabledDashboardTab
                        dashboardKey={report.id}
                        dashboardName={report.name}
                        dashboardId={report.id}
                        iconName='infooutlined'
                        iconColor='chambray'
                      />
                    </div>
                  </Tooltip>
                )
              );
            }
            return (
              <Tooltip
                interactive
                key={`${idx}-allReports`}
                disableHoverListener={!isMetricReport}
                {...(isMetricReport ? {} : { open: false })}
                title={
                  isMetricReport ? (
                    <DashboardSummaryTooltip dashboard={report} />
                  ) : (
                    ''
                  )
                }
                placement='right'
              >
                <div>
                  <DashboardTab
                    key={`${idx}-allReports-${report.account_id || 'linearB'}`}
                    dashboardKey={report.id}
                    selected={selectedReport && report.id === selectedReport.id}
                    onClick={() => {
                      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                        page: TRACKING.PAGES.METRICS,
                        click_type: report.name,
                        dashboard_name: report.name,
                        dashboard_id: report.id,
                        internal_type: report.internal_type
                      });
                      return onSelectReport(report);
                    }}
                    dashboardName={report.name}
                    dashboardId={report.id}
                    pinnedDashboards={pinnedDashboards}
                    onPinDashboard={dashboards => {
                      updatePinnedBoards(dashboards)(dispatch);
                    }}
                    isPublic={report.is_public}
                    {...(report.is_beta
                      ? {
                          badge: 'beta'
                        }
                      : {})}
                    isCustomPmReport={isCustomPmReport}
                    isNew={report.is_new}
                    containerWidth={containerWidth}
                  />
                </div>
              </Tooltip>
            );
          })}
        </div>
      </StyledScrollableSection>
    </StyledPerformanceSidebar>
  );
};

export default PerformanceSideBar;
